import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';

import replaceNonAlphanumeric from '../functions/replaceNonAlphanumeric';
import HorizontalTabs from './HorizontalTabs';


function ProductCardTabFooter({
    initialTabIndex = null,
    tabBar,
    tabPanelContent,
    productId,

    borderRadius = '8px',
    className,
}) {
    const [ activeTab, setActiveTab ] = useState(null);

    // set active tab if we have an initialTabIndex
    useEffect(() => {
        if (tabPanelContent[initialTabIndex]?.content) {
            setActiveTab(tabBar[initialTabIndex].value);
        }
    }, [ initialTabIndex ]);

    const prodId = replaceNonAlphanumeric(productId);
    const tabIdPrefix = `tab_${prodId}`;
    const tabPanelIdPrefix = `panel_${prodId}`;

    function handleTabChange(selectedKey) {
        setActiveTab(selectedKey);
    }

    function getTabPanelIndexFromKey(key) {
        return tabPanelContent.findIndex(tabContent => tabContent.key === key);
    }


    return (
        <Container
            className={className}
            borderRadius={borderRadius}
        >
            <HorizontalTabs
                className={classNames('rh-bg-stone-lightest', {
                    'rounded-bottoms': activeTab === null,
                    'flat-bottom': activeTab !== null,  // when showing a tab
                })}
                variant="chevrons"
                enableAccordionToggle={true}

                onTabChange={handleTabChange}

                initialTabIndex={initialTabIndex}
                tabIdPrefix={tabIdPrefix}
                tabPanelIdPrefix={tabPanelIdPrefix}
                tabs={tabBar}
            />

            <For
                each="tabIndex"
                of={tabBar}
                index="index"
            >
                <div
                    role="tabpanel"
                    className={classNames('rounded-bottoms rh-bg-coconut', {
                        'rh-display-none': activeTab !== tabIndex.value,
                    })}
                    aria-hidden={activeTab !== tabIndex.value}
                    id={`${tabPanelIdPrefix}_${tabIndex.value}`}
                    aria-labelledby={`${tabIdPrefix}_${tabIndex.value}`}
                    key={index}
                >
                    {tabPanelContent[getTabPanelIndexFromKey(tabIndex.value)].content}
                </div>
            </For>
        </Container>
    );
}


ProductCardTabFooter.propTypes = {
    initialTabIndex: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.oneOf([ null ]),
    ]),
    tabBar: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.node,
            value: PropTypes.any,
        }),
    ).isRequired,
    tabPanelContent: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            content: PropTypes.node.isRequired,
        }).isRequired,
    ),
    productId: PropTypes.string,

    borderRadius: PropTypes.string,
    className: PropTypes.string,
};


const Container = styled.div`
    // provide border-radius on bottom-left and -right when the HorizontalTab is the bottom of the card
    // or when the tab content we provide is the bottom
    .rounded-bottoms {
        ${({ borderRadius }) => `border-radius: 0 0 calc(${borderRadius} - 1px) calc(${borderRadius} - 1px)`};
    }

    > .flat-bottom {
        border-radius: unset;
    }
`;


export default observer(ProductCardTabFooter);
